<template>
  <!-- Message Input Node -->
  <div v-if="data.type === 'messageInput'" class="n8n-node message-node">

    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="20">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon"
        style="width: 60px; height: 60px;border-radius: 50%; background-color: transparent;border: none;">
        <v-icon size="40">mdi-chat</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Message</div>
      <div class="node-subtitle">User Input</div>
    </div>
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- API Node -->
  <div v-else-if="data.type === 'api'" class="n8n-node api-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-api</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Call</div>
      <div class="node-subtitle">External Service</div>
    </div>
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Response Output Node -->
  <div v-else-if="data.type === 'response'" class="n8n-node response-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-message-reply-text</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Response</div>
      <div class="node-subtitle">Bot Output</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-response-handle', 'target')"
      style="border-radius: 50%;" :position="Position.Left" id="input" />
  </div>

  <!-- AI Model Node -->
  <div v-else-if="data.type === 'model'" class="ai-agent-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="ai-agent-content" @click="toggleModelSelector">
      <div class="ai-agent-icon">
        <v-icon size="28" color>mdi-robot</v-icon>
      </div>
      <div class="ai-agent-text">
        <div class="ai-agent-title">AI Model</div>
        <div class="ai-agent-subtitle">{{ getModelDisplayName() }}</div>
      </div>
    </div>

    <!-- Base Model Prompt Indicator -->
    <div v-if="data.useBaseModelPrompt" class="base-prompt-indicator">
      <v-icon size="15" color="#1a992f" class="mr-1">mdi-text-box-check</v-icon>
      <span style="margin-bottom: 2px;font-weight: 400; font-size: 12px; color:#1a992f;">Base Prompt</span>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
    <Handle :class="getHandleClass('output-bottom', 'multi-edge-handle')" type="source" :position="Position.Bottom"
      id="output-bottom" />

  </div>

  <!-- Intent AI Model Node -->
  <div v-else-if="data.type === 'intentAI'" class="n8n-node intent-ai-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-target-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Intent AI</div>
      <div class="node-subtitle">{{ intentModelDisplay }}</div>
    </div>
    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'intentai-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Intent Node -->
  <div v-else-if="data.type === 'intent'" class="intent-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="intent-header-section">
      <div class="intent-header">
        <div class="intent-icon">
          <v-icon size="24">mdi-crosshairs-question</v-icon>
        </div>
        <div class="intent-info">
          <div class="intent-title">Intent Router</div>
          <div class="intent-subtitle">{{ data.intentGroupName || 'New Intent Group' }}</div>
        </div>
      </div>
    </div>

    <!-- Intents Section as Table -->
    <div class="intents-container" v-if="data.intents && data.intents.length > 0">
      <table class="intent-table">
        <template v-for="(intent, intentIndex) in data.intents" :key="intent.id">
          <!-- Main Action Row -->
          <tr v-if="intent.mainAction" class="handle-row">
            <td :rowspan="getIntentActionsCount(intent)" class="intent-cell">
              <div class="intent-content">
                <v-avatar size="small" color="indigo" class="intent-number">
                  <span class="text-caption">{{ intentIndex + 1 }}</span>
                </v-avatar>
                <div class="intent-details">
                  <div class="intent-name">{{ intent.name.length > 50 ? intent.name.substring(0, 50) + '...' :
                    intent.name }}</div>
                  <span v-if="intent.priority || intent.priority === 0"
                    style="color: #00000055;font-size: 12px;height: 5px;">
                    Prority: {{ intent.priority }}
                  </span>
                  <!-- <div class="intent-prompt-preview">{{ getPromptPreview(intent.prompt) }}</div> -->
                  <!-- <v-chip size="x-small" color="grey" variant="tonal" class="output-count">
                    {{ getIntentActionsCount(intent) }} outputs
                  </v-chip> -->
                </div>
              </div>
            </td>
            <td class="handle-cell">
              <div class="handle-content main-handle">
                <Handle type="source"
                  :class="getHandleClass(`${intent.id}-main`, 'intent-action-handle main-action-handle')"
                  :position="Position.Right" :id="`${intent.id}-main`">
                  <span class="handle-label">{{ intent.mainAction.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>

          <!-- Parallel Action Rows -->
          <tr v-for="(parallel, parallelIndex) in intent.parallelActions" :key="parallel.id" class="handle-row">
            <td class="handle-cell">
              <div class="handle-content parallel-handle">
                <Handle type="source"
                  :class="getHandleClass(`${intent.id}-${parallel.id}`, 'intent-action-handle parallel-action-handle')"
                  :position="Position.Right" :id="`${intent.id}-${parallel.id}`">
                  <span class="handle-label">{{ parallel.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>
          <tr v-if="intentIndex < data.intents.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>


    <!-- Fallback Output Section -->
    <div class="intent-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">Fallback</div>
            <div class="fallback-output-description">No intent match</div>
          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('intent-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="intent-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')" :position="Position.Bottom"
      id="output-bottom" />
  </div>

  <!-- Smart Intent Node -->
  <div v-else-if="data.type === 'smartIntent'" class="smart-intent-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="smart-intent-header-section">
      <div class="smart-intent-header">
        <div class="smart-intent-icon">
          <v-icon size="24">mdi-robot-confused</v-icon>
        </div>
        <div class="smart-intent-info">
          <div class="smart-intent-title">Smart Intent</div>
          <div class="smart-intent-subtitle">{{ data.smartIntentGroupName || 'New Smart Intent Group' }}</div>
        </div>
      </div>
    </div>

    <!-- Smart Intents Section as Table -->
    <div class="smart-intents-container" v-if="data.smartIntents && data.smartIntents.length > 0">
      <table class="smart-intent-table">
        <template v-for="(smartIntent, smartIntentIndex) in data.smartIntents" :key="smartIntent.id">
          <!-- Main Action Row -->
          <tr v-if="smartIntent.mainAction" class="handle-row">
            <td :rowspan="1" class="smart-intent-cell">
              <div class="smart-intent-content">
                <v-avatar size="small" color="indigo" class="smart-intent-number">
                  <span class="text-caption">{{ smartIntentIndex + 1 }}</span>
                </v-avatar>
                <div class="smart-intent-details">
                  <div class="smart-intent-name">{{ smartIntent.name.length > 50 ? smartIntent.name.substring(0, 50) +
                    '...' : smartIntent.name }}</div>
                  <span v-if="smartIntent.priority || smartIntent.priority === 0"
                    style="color: #00000055;font-size: 12px;height: 5px;">
                    Priority: {{ smartIntent.priority }}
                  </span>
                  <!-- AI Model Info Display -->
                  <div
                    v-if="smartIntent.mainAction.useAI && (smartIntent.mainAction.provider || smartIntent.mainAction.model)"
                    class="ai-config-info" style="color: #4f46e5; font-size: 11px; margin-top: 2px;">
                    <v-icon size="12" color="#4f46e5">mdi-robot</v-icon>
                    {{ getSmartIntentActionModelDisplay(smartIntent.mainAction) }}
                  </div>

                  <!-- Knowledge Sources Icons for this Smart Intent -->
                  <div v-if="hasSmartIntentKnowledgeSources(smartIntent)" class="knowledge-sources-indicators"
                    style="margin-top: 4px;">
                    <div class="knowledge-icons-row">
                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableDocuments" location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="blue" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-file-document</v-icon>
                          </v-avatar>
                        </template>
                        <span>Documents Knowledge</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableDatabaseIntegration"
                        location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="green" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-database</v-icon>
                          </v-avatar>
                        </template>
                        <span>Database Integration</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableProducts" location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="purple" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-package-variant</v-icon>
                          </v-avatar>
                        </template>
                        <span>Products Knowledge</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableApiKnowledge"
                        location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="orange" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-rocket-launch</v-icon>
                          </v-avatar>
                        </template>
                        <span>API Knowledge</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </td>

            <td class="handle-cell">
              <div class="handle-content main-handle">
                <Handle type="source"
                  :class="getHandleClass(`${smartIntent.id}-main`, 'smart-intent-action-handle main-action-handle')"
                  :position="Position.Right" :id="`${smartIntent.id}-main`">
                  <span class="handle-label">{{ smartIntent.mainAction.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>

          <tr v-if="smartIntentIndex < data.smartIntents.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Fallback Output Section -->
    <div class="smart-intent-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">
              Fallback
              <!-- Fallback AI Model Info Display -->
              <div v-if="data.smartIntentFallback && data.smartIntentFallback.useAI && (data.smartIntentFallback.provider || data.smartIntentFallback.model)"
                class="ai-config-info" style="color: #4f46e5; font-size: 11px; margin-top: 2px;">
                <div style="display: flex; flex-direction: column;">
                  <div>
                    <v-icon size="12" color="#4f46e5">mdi-robot</v-icon>
                    {{ getSmartIntentFallbackModelDisplay() }}
                  </div>
                  <!-- Knowledge Sources Icons for Fallback -->
                  <div v-if="hasSmartIntentFallbackKnowledgeSources()" class="knowledge-sources-icons mt-1">
                    <v-tooltip v-if="hasFallbackDocumentsKnowledge()" text="Documents Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="blue" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-file-document</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackDatabaseKnowledge()" text="Database Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="green" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-database</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackProductsKnowledge()" text="Products Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="purple" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-package-variant</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackApiKnowledge()" text="API Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="orange" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-rocket-launch</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div class="fallback-output-description">No intent match</div>

          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('smart-intent-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="smart-intent-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <!-- Bottom Handle for IntentAI connection -->
    <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')" :position="Position.Bottom"
      id="output-bottom" />

    <!-- <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')"
      :position="Position.Bottom" id="output-bottom"/> -->
  </div>

  <!-- Condition Node -->
  <div v-else-if="data.type === 'condition'" class="n8n-node condition-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-swap-horizontal-bold</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">If</div>
      <div class="node-subtitle">Logic Check</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output-true', 'output-handle condition-true-output-handle')"
      :position="Position.Right" style="top: 25%;" id="output-true" />
    <Handle type="source" :class="getHandleClass('output-false', 'output-handle condition-false-output-handle')"
      :position="Position.Right" style="top: 75% !important;" id="output-false" />

    <!-- Output Labels for Condition -->
    <div class="output-labels condition-labels">
      <span class="output-label condition-true-label" style="top: 0;">{{ data.trueLabel || 'True' }}</span>
      <span class="output-label condition-false-label">{{ data.falseLabel || 'False' }}</span>
    </div>
  </div>

  <!-- Delay Node -->
  <div v-else-if="data.type === 'delay'" class="n8n-node delay-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-timer</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Delay</div>
      <div class="node-subtitle">
        <span v-if="data.delayType === 'fixed'">
          {{ data.delayValue || 5 }} {{ data.delayUnit || 'seconds' }}
        </span>
        <span v-else-if="data.delayType === 'random'">
          {{ data.randomMin || 1 }}-{{ data.randomMax || 10 }} {{ data.delayUnit || 'seconds' }}
        </span>
        <span v-else-if="data.delayType === 'variable'">
          Variable Delay
        </span>
        <span v-else>
          Time Wait
        </span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Switch Node -->
  <div v-else-if="data.type === 'switch'" class="switch-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="switch-header-section">
      <div class="switch-header">
        <div class="switch-icon">
          <v-icon size="24">mdi-arrow-decision</v-icon>
        </div>
        <div class="switch-info">
          <div class="switch-title">Switch</div>
          <div class="switch-subtitle">Multi-Path</div>
        </div>
      </div>
    </div>

    <!-- Switch Outputs Section as Table -->
    <div class="switch-outputs-container" v-if="data.switchOutputs && data.switchOutputs.length > 0">
      <table class="switch-table">
        <template v-for="(output, outputIndex) in data.switchOutputs" :key="output.id">
          <tr class="handle-row">
            <td class="switch-output-cell">
              <div class="switch-output-content">
                <v-avatar size="small" color="indigo" class="output-number">
                  <span class="text-caption">{{ outputIndex + 1 }}</span>
                </v-avatar>
                <div class="switch-output-details">
                  <div class="switch-output-name">{{ output.name.length > 40 ? output.name.substring(0, 40) + '...' :
                    output.name }}</div>
                  <div class="switch-output-condition">{{ output.condition.length > 35 ? output.condition.substring(0,
                    35) + '...' : output.condition }}</div>
                </div>
              </div>
            </td>
            <td class="handle-cell" style="width: 10px;">
              <div class="handle-content switch-handle">
                <Handle type="source" :class="getHandleClass(`switch-output-${output.id}`, 'switch-output-handle')"
                  :position="Position.Right" :id="`switch-output-${output.id}`">
                </Handle>
              </div>
            </td>
          </tr>
          <tr v-if="outputIndex < data.switchOutputs.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; width: 10px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Default Case Output (Fixed Handle) -->
    <div class="switch-default-output">
      <div class="default-output-row">
        <div class="default-output-content">
          <v-avatar size="small" color="orange" class="default-output-number">
            <span class="text-caption">D</span>
          </v-avatar>
          <div class="default-output-details">
            <div class="default-output-name">Default Case</div>
            <div class="default-output-description">Fallback path</div>
          </div>
        </div>
        <div class="default-handle-cell">
          <Handle type="source" :class="getHandleClass('switch-default', 'default-output-handle')"
            :position="Position.Right" id="switch-default">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
  </div>

  <!-- Webhook Node -->
  <div v-else-if="data.type === 'webhook'" class="n8n-node webhook-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-webhook</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Webhook</div>
      <div class="node-subtitle">HTTP Notify</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Email Node -->
  <div v-else-if="data.type === 'email'" class="n8n-node email-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Action Badge -->
    <div class="action-badge">
      <v-icon size="10">mdi-lightning-bolt</v-icon>
      <span>ACTION</span>
    </div>

    <!-- Status Indicator -->
    <div class="status-indicator" :class="{ 'configured': data.toEmail && data.subject }">
      <div class="status-dot"></div>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <div class="icon-bg">
          <v-icon size="28">mdi-email-send</v-icon>
        </div>
      </div>
      <div class="node-details">
        <span v-if="data.emailProvider === 'mailgun'" class="provider-tag mailgun">Mailgun</span>
        <span v-else class="provider-tag smtp">SMTP</span>
        <span v-if="data.ccEmails && data.ccEmails.length > 0" class="cc-indicator">+{{ data.ccEmails.length }}
          CC</span>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Email</div>
      <div class="node-subtitle">
        <span v-if="data.toEmail">{{ data.toEmail.length > 20 ? data.toEmail.substring(0, 20) + '...' : data.toEmail
          }}</span>
        <span v-else>Configure recipient</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- API Trigger Node -->
  <div v-else-if="data.type === 'apiTrigger'" class="n8n-node api-trigger-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-rocket-launch</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Trigger</div>
      <div class="node-subtitle">External Action</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <!-- Hide output handle if connected from parallel action -->
    <Handle v-if="!isConnectedFromParallelAction()" type="source"
      :class="getHandleClass('output-success', 'output-handle')" :position="Position.Right" id="output-success" />
    <!-- <Handle type="source" class="output-handle" :position="Position.Bottom" id="output-error" /> -->
  </div>

  <!-- Contact Agent Node -->
  <div v-else-if="data.type === 'contactAgent'" class="n8n-node contact-agent-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-account-supervisor</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Contact Agent</div>
      <div class="node-subtitle">To disabled bot</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <!-- Hide output handle if connected from parallel action -->
    <Handle v-if="!isConnectedFromParallelAction()" type="source"
      :class="getHandleClass('output-success', 'output-handle')" :position="Position.Right" id="output-success" />
    <!-- No output handle as this is typically an end state -->
  </div>

  <!-- Documents Node -->
  <div v-else-if="data.type === 'documents'" class="n8n-node documents-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-file-document-multiple</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Documents</div>
      <div class="node-subtitle">Document Search</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Database Integration Node -->
  <div v-else-if="data.type === 'databaseIntegration'" class="n8n-node database-integration-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-database-search</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">DB Integration</div>
      <div class="node-subtitle">Knowledge Base</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Knowledge API Node -->
  <div v-else-if="data.type === 'apiknowledge'" class="n8n-node knowledge-api-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-rocket-launch</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Knowledge</div>
      <div class="node-subtitle">API Response Knowledge</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Products Node -->
  <div v-else-if="data.type === 'products'" class="n8n-node products-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-package-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Products</div>
      <div class="node-subtitle">Product Catalog</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- SharePoint Node -->
  <div v-else-if="data.type === 'sharepoint'" class="n8n-node sharepoint-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-microsoft-sharepoint</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">SharePoint</div>
      <div v-if="sharepointSiteName || sharepointFileName">
        <div v-if="sharepointSiteName" class="node-subtitle" style="font-weight: 500;">{{ sharepointSiteName }}</div>
        <div v-if="sharepointFileName" class="node-subtitle" style="font-weight: 400;">{{ sharepointFileName }}</div>
      </div>
      <div v-else class="node-subtitle">{{ sharepointDisplayText }}</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>
</template>

<script>
import { Handle, Position } from '@vue-flow/core'

export default {
  name: 'FlowNode',
  components: {
    Handle,
  },
  inject: {
    aiModelOptions: {
      default: () => []
    },
    flowEdges: {
      default: () => []
    }
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: true,
      default: () => ({
        type: 'default',
        label: '',
      }),
    },
  },
  setup(props) {
    // Expose Position for template usage
    return { Position }
  },
  data() {
    return {
      showModelSelector: true,
      selectedModel: this.data.selectedModel || null,
    };
  },
  watch: {
    'data.selectedModel': {
      handler(newVal) {
        this.selectedModel = newVal;
      },
      immediate: true
    },
    // Watch for changes in flow edges to handle apiTrigger logic
    flowEdges: {
      handler() {
        // Only for specific node types; avoid scheduling on every node for every edge change
        if (this.data.type === 'apiTrigger' || this.data.type === 'contactAgent') {
          this.$nextTick(() => {
            this.removeOutgoingEdgesIfFromParallelAction();
          });
        }
      },
      // Edge array is typically replaced; deep watch not needed and is expensive
      deep: false,
      immediate: false
    }
  },
  mounted() {
    // Check and remove outgoing edges on component mount
    this.$nextTick(() => {
      this.removeOutgoingEdgesIfFromParallelAction();
    });
  },
  computed: {
    isViewMode() {
      return this.$route?.query?.mode === 'view';
    },
    intentModelDisplay() {
      // ใช้ intentai properties (prioritize new format, fallback to legacy)
      const provider = this.data.intentaiProvider;
      const model = this.data.intentaiModel;

      if (!provider && !model) return 'No Model Selected';

      // แสดงทั้ง provider และ model ถ้ามีทั้งคู่
      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model Selected';
    },
    sharepointSiteName() {
      if (this.data.sharepoint.siteUrl) {
        return this.extractSiteNameFromUrl(this.data.sharepoint.siteUrl);
      }
      return '';
    },
    sharepointFileName() {
      if (this.data.sharepoint.filename) {
        return this.data.sharepoint.filename;
      }
      return '';
    },
    sharepointDisplayText() {
      // เก็บไว้เพื่อความเข้ากันได้เดิม - แสดง "File Access" หากไม่มีข้อมูล
      if (!this.data.sharepoint.siteUrl && !this.data.sharepoint.filename) {
        return 'File Access';
      }
      return '';
    }
  },
  methods: {
    toggleModelSelector() {
      this.showModelSelector = !this.showModelSelector;
    },
    onModelChange(value) {
      this.selectedModel = value;
      this.$emit('update:data', this.id, {
        selectedModel: value
      });
      this.showModelSelector = false;
    },
    getModelDisplayName() {
      // ใช้ข้อมูลจาก data แทน selectedModel
      if (this.data.provider && this.data.model) {
        return `${this.data.provider}: ${this.formatModelName(this.data.model)}`;
      } else if (this.data.provider) {
        return this.data.provider;
      } else if (this.data.model) {
        return this.formatModelName(this.data.model);
      }
      return 'Select Model';
    },
    formatModelName(modelName) {
      if (!modelName) return '';
      // ถ้ามี | ให้เอาส่วนหลัง | มาแสดง (เช่น gpt-4|gpt-4 จะแสดงแค่ gpt-4)
      if (modelName.includes('|')) {
        const parts = modelName.split('|');
        return parts[parts.length - 1];
      }
      // ตัดให้สั้นลงถ้ายาวเกินไป
      return modelName.length > 20 ? modelName.substring(0, 20) + '...' : modelName;
    },
    extractSiteNameFromUrl(siteUrl) {
      if (!siteUrl) return '';
      
      try {
        // แปลง URL string เป็น URL object
        const url = new URL(siteUrl);
        
        // ถ้าเป็น SharePoint URL แบบปกติ (เช่น https://company.sharepoint.com/sites/sitename)
        const pathParts = url.pathname.split('/').filter(part => part.length > 0);
        
        // ถ้า path มี sites/ ให้เอาส่วนหลัง sites
        if (pathParts.length >= 2 && pathParts[0] === 'sites') {
          return pathParts[1];
        }
        
        // ถ้าไม่ใช่ รูปแบบ sites/ ให้ใช้ hostname แทน
        // เช่น https://company-my.sharepoint.com -> company
        const hostname = url.hostname;
        if (hostname.includes('.sharepoint.com')) {
          const subdomain = hostname.split('.')[0];
          // ถ้ามี - ให้เอาส่วนแรก
          return subdomain.split('-')[0];
        }
        
        // ถ้าไม่ตรงกับรูปแบบไหนเลย ให้ return hostname
        return hostname;
      } catch (error) {
        // ถ้า URL ไม่ถูกต้อง ให้ return string เดิม
        return siteUrl;
      }
    },
    // Smart Intent specific methods
    getSmartIntentActionModelDisplay(action) {
      if (!action || !action.useAI) return '';

      const provider = action.provider;
      const model = action.model;

      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model';
    },
    getSmartIntentFallbackModelDisplay() {
      const fallback = this.data.smartIntentFallback;
      if (!fallback || !fallback.useAI) return '';

      const provider = fallback.provider;
      const model = fallback.model;

      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model';
    },

    // Knowledge Sources Detection Methods
    hasKnowledgeSources() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && (
          action.enableDocuments ||
          action.enableDatabaseIntegration ||
          action.enableProducts ||
          action.enableApiKnowledge
        );
      });
    },

    hasDocumentsKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableDocuments;
      });
    },

    hasDatabaseKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableDatabaseIntegration;
      });
    },

    hasProductsKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableProducts;
      });
    },

    hasApiKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableApiKnowledge;
      });
    },

    // Individual Smart Intent Knowledge Sources Detection
    hasSmartIntentKnowledgeSources(smartIntent) {
      if (!smartIntent || !smartIntent.mainAction) return false;

      const action = smartIntent.mainAction;
      return action.enableDocuments ||
        action.enableDatabaseIntegration ||
        action.enableProducts ||
        action.enableApiKnowledge;
    },

    // Smart Intent Fallback Knowledge Sources Detection
    hasSmartIntentFallbackKnowledgeSources() {
      const fallback = this.data.smartIntentFallback;
      if (!fallback) return false;

      return fallback.enableDocuments ||
        fallback.enableDatabaseIntegration ||
        fallback.enableProducts ||
        fallback.enableApiKnowledge;
    },

    hasFallbackDocumentsKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableDocuments;
    },

    hasFallbackDatabaseKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableDatabaseIntegration;
    },

    hasFallbackProductsKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableProducts;
    },

    hasFallbackApiKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableApiKnowledge;
    },

    deleteNode() {
      // Add click.stop to prevent event bubbling
      this.$emit('delete-node', this.id);
    },
    getPromptPreview(prompt) {
      if (!prompt) return '';
      return prompt.length > 30 ? prompt.substring(0, 30) + '...' : prompt;
    },
    getIntentActionsCount(intent) {
      let count = 0;
      if (intent.mainAction) count++;
      if (intent.parallelActions) count += intent.parallelActions.length;
      return count;
    },
    getSwitchOutputsCount(switchData) {
      return switchData.switchOutputs ? switchData.switchOutputs.length : 0;
    },
    // Check if a handle is connected
    isHandleConnected(handleId, handleType = 'source') {
      // Get edges from injected flowEdges (Vue 3 way)
      const edges = this.flowEdges || [];

      return edges.some(edge => {
        if (handleType === 'source') {
          return edge.source === this.id && edge.sourceHandle === handleId;
        } else {
          return edge.target === this.id && edge.targetHandle === handleId;
        }
      });
    },

    // Check if current node is connected from a parallel action
    isConnectedFromParallelAction() {
      const edges = this.flowEdges || [];

      // Find any edge that has this node as target
      const incomingEdge = edges.find(edge => edge.target === this.id);

      if (!incomingEdge) return false;

      // Check if the source handle contains patterns that indicate parallel action
      const sourceHandle = incomingEdge.sourceHandle;

      // Pattern: sourceHandle format is like "intentId-parallelId" for parallel actions
      // Main actions typically use format "intentId-main" or just "intentId"
      if (sourceHandle && sourceHandle.includes('-') && !sourceHandle.includes('-parallel-')) {
        return false;
      }
      return true;
    },

    // Remove outgoing edges from apiTrigger and contactAgent when connected from parallel action
    removeOutgoingEdgesIfFromParallelAction() {
      // Only apply this logic for apiTrigger and contactAgent nodes
      if (this.data.type !== 'apiTrigger' && this.data.type !== 'contactAgent') return;

      // Check if this node is connected from parallel action
      if (!this.isConnectedFromParallelAction()) return;

      const edges = this.flowEdges || [];

      // Find all outgoing edges from this node
      const outgoingEdges = edges.filter(edge => edge.source === this.id);

      // If there are outgoing edges, remove them using the parent component method
      if (outgoingEdges.length > 0) {
        outgoingEdges.forEach(edge => {
          // Emit event to parent component to remove the edge
          this.$emit('remove-outgoing-edge', edge.id);
        });
      }
    },

    // Get dynamic class for handles based on connection status
    getHandleClass(handleId, baseClass, handleType = 'source') {
      const classes = [baseClass];
      if (this.isHandleConnected(handleId, handleType)) {
        classes.push('connected');
      }
      return classes.join(' ');
    },
    // Add method to check if handle can accept connection
    isValidConnectionTarget(handleId, sourceNodeId, sourceHandleId) {
      // Get parent flow instance to access validation
      const flowInstance = this.$parent;
      if (!flowInstance || !flowInstance.validateConnection) {
        return true;
      }
      const mockConnection = {
        source: sourceNodeId,
        target: this.id,
        sourceHandle: sourceHandleId,
        targetHandle: handleId
      };
      const validation = flowInstance.validateConnection(mockConnection);
      return validation.isValid;
    },
    // Add visual feedback for handles
    // onHandleMouseEnter(handleElement, handleId) {
    //     // Add logic to show connection preview
    //     const isValid = this.isValidConnectionTarget(handleId);
    //     if (isValid) {
    //         handleElement.classList.add('valid-connection');
    //     } else {
    //         handleElement.classList.add('invalid-connection');
    //     }
    // },
    // onHandleMouseLeave(handleElement) {
    //     handleElement.classList.remove('valid-connection', 'invalid-connection');
    // }
  }
}
</script>

<style scoped>
/* N8N Style Node Base */
.n8n-node {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 100px;
  width: 100px;
  height: 100px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  /* Changed from hidden to visible */
  margin: 15px;
  /* Add margin to provide space for handles and delete button */
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.n8n-node:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  /* transform: translateY(-2px); */
  border-color: #d1d5db;
  transition: all 0.2s ease;
}

.selected .n8n-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

.node-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
  /* Keep full space for icon only */
}

.node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f8fafc;
  color: #64748b;
  transition: all 0.2s ease;
}

.node-body {
  position: absolute;
  /* Position outside the node box */
  top: calc(100% + 4px);
  /* Start just below the node box */
  left: 50%;
  transform: translateX(-50%);
  /* Center horizontally */
  text-align: center;
  background: transparent;
  white-space: nowrap;
  pointer-events: none;
  /* Don't interfere with node interactions */
  z-index: 10;
}

.node-subtitle {
  font-size: 10px;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Legacy minimal nodes for backward compatibility */
.minimal-node {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 160px;
  cursor: pointer;
  overflow: visible;
  /* Changed from default to visible */
  margin: 15px;
  /* Add margin to provide space for handles and delete button */
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.minimal-node:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.node-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
}

.node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #6b7280;
}

.node-info {
  flex: 1;
  min-width: 0;
}

.node-title {
  font-size: 16px;
  font-weight: 600;
  color: #51565f;
  margin-bottom: 2px;
  line-height: 1.2;
}

.node-subtitle {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


/* Modern Intent Node */
.intent-modern-node {
  background: white;
  border: 5px solid #c0a4ff;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(145, 27, 255, 0.699);
  transition: all 0.2s ease;
  position: relative;
  min-width: 300px;
  width: 300px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.intent-modern-node:hover {
  border-color: #a984ff;
}

.selected .intent-modern-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Header Section */
.intent-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.intent-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.intent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.25);
}

.intent-info {
  flex: 1;
  min-width: 0;
}

.intent-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  margin-bottom: 2px;
}

.intent-subtitle {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Intents Container */
.intents-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

/* Intent Table Styles */
.intent-table {
  width: 100%;
  border-collapse: collapse;
  background: transparent;
}

.handle-row {
  position: relative;
  height: 40px;
  border-radius: 15px !important;
}

.handle-row:last-child {
  border-bottom: none;
}

.intent-cell {
  padding: 8px 12px;
  vertical-align: top;
  width: 70%;
  max-width: 70%;
  border-radius: 15px !important;
}

.handle-cell {
  padding: 0;
  vertical-align: middle;
  position: relative;
  width: 10px;
}

.intent-content {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
}

.intent-number {
  flex-shrink: 0;
}

.intent-details {
  flex: 1;
  min-width: 0;
  max-width: 120px;
}

.intent-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  line-height: 1.2;
}

.intent-prompt-preview {
  font-size: 11px;
  color: #64748b;
  line-height: 1.3;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.output-count {
  margin-top: 4px;
}

.handle-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  position: relative;
}

.handle-label {
  position: absolute;
  right: 10px;
  transform: translateY(-50%);
  height: 8px;
  text-wrap: nowrap;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  padding-right: 8px;
}

.main-handle .handle-label {
  color: #ad7eff;
  right: 15px;
}

.parallel-handle .handle-label {
  color: #1698e4;
  right: 15px;
}

/* Intent Actions Handles */
.intent-action-handle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.main-action-handle {
  background: #ffffff;
  border: 3px solid #9655fd;
}

.main-action-handle:hover {
  background: #9655fd;
}

.parallel-action-handle {
  background: #ffffff;
  border: 3px solid #1698e4;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.parallel-action-handle:hover {
  background: #1698e4;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

/* Connected styles for intent action handles */
.main-action-handle.connected {
  background: #9655fd !important;
}

.parallel-action-handle.connected {
  background: #1698e4 !important;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}


.intent-action-handle:hover::after {
  opacity: 1;
}

/* Node Type Specific Styles */
.message-node {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 4px solid #53ca7e;
  border-radius: 15px 50px 50px 15px;
  color: #166534;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.26);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.message-node:hover {
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.08);
  border-color: #119400;
}


.message-node .node-icon {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border: 2px solid rgba(34, 197, 94, 0.2);
  backdrop-filter: blur(10px);
}

.api-node {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 4px solid #fbbf24;
  border-radius: 15px 50px 50px 15px;
  color: #92400e;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.1);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.api-node:hover {
  box-shadow: 0 8px 16px rgba(245, 158, 11, 0.15);
  border-color: #f59e0b;
}

.api-node .node-icon {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 2px solid rgba(245, 158, 11, 0.2);
  backdrop-filter: blur(10px);
}

.model-node {
  border-left: 4px solid #8b5cf6;
}

.model-node .node-icon {
  background: #f5f3ff;
  color: #8b5cf6;
}

.model-node .node-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
  /* Keep original padding for AI model */
}

.model-node .node-body {
  padding: 8px 12px 16px 12px;
  /* Keep original padding for AI model */
  text-align: center;
  background: rgba(249, 250, 251, 0.5);
  /* Keep background for AI model */
  border-top: 1px solid #f1f5f9;
  /* Keep border for AI model */
}

.model-node.expanded {
  min-width: 220px;
}

/* AI Agent Node - Modern Minimal Style #3b82f6*/
.ai-agent-node {
  background: #ffffff80;
  backdrop-filter: blur(10px);
  border: 5px solid #8bb8ff;
  border-radius: 16px;
  box-shadow: 3px 5px 5px rgb(11, 68, 255) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  width: 220px;
  height: 90px;
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: visible;
  margin: 15px;
}

.ai-agent-node:hover {
  box-shadow:
    0 4px 12px rgba(59, 130, 246, 0.15),
    0 0 0 1px rgba(59, 130, 246, 0.2);
  border-color: #5f92ff;
}

.selected .ai-agent-node {
  /* box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important; */

  border-color: #2563eb;
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

.intent-node {
  border-left: 4px solid #3b82f6;
}

.intent-node .node-icon {
  background: #eff6ff;
  color: #3b82f6;
}

.response-node {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 4px solid #f87c7c;
  border-radius: 50px 15px 15px 50px;
  color: #991b1b;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.08);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.response-node:hover {
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.12);
  border-color: rgb(243, 50, 50);
}

.response-node .node-icon {
  background: #fee2e2;
  color: #dc2626;
  border: 2px solid #fecaca;
  backdrop-filter: blur(10px);
}

.guardrail-ai-node {
  background: white;
  border: 5px solid rgba(19, 153, 26, 0.664);
}

.guardrail-ai-node:hover {
  border: 5px solid rgba(10, 190, 19, 0.664);
}

.guardrail-ai-node .node-icon {
  background: transparent;
  color: rgb(24, 179, 24);
  border: none;
}

.intent-ai-node {
  background: white;
  border: 5px solid rgba(149, 55, 211, 0.664);
}

.intent-ai-node:hover {
  border: 5px solid rgba(94, 10, 190, 0.664);
}

.intent-ai-node .node-icon {
  background: transparent;
  color: rgb(146, 45, 230);
  border: none;
}

.database-node {
  background-color: #a6cbf0;
  border: 4px solid #ffffff;
}

.database-node .node-icon {
  background: #f0f9ff;
  color: #0ea5e9;
}

/* Text Processor Node - Modern Minimal Style */
.text-processor-node {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 2px solid #a855f7;
  border-radius: 16px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 0 0 1px rgba(168, 85, 247, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  width: 220px;
  height: 90px;
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: visible;
  margin: 15px;
}

.text-processor-node:hover {
  transform: translateY(-1px);
  box-shadow:
    0 4px 12px rgba(168, 85, 247, 0.15),
    0 0 0 1px rgba(168, 85, 247, 0.2);
  border-color: #9333ea;
}

.text-processor-content {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  gap: 12px;
  width: 100%;
}

.text-processor-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.25);
}

.text-processor-text {
  flex: 1;
  min-width: 0;
}

.text-processor-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.025em;
}

.text-processor-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.condition-node {
  background: linear-gradient(135deg, #50ff50 0%, #069606 100%);
}

.condition-node .node-icon {
  background: transparent;
  color: #ffffff;
}

.delay-node {
  background: linear-gradient(135deg, #7bd3ff 0%, #088fce 100%);
}

.delay-node .node-icon {
  background: #0284c7;
  color: #ffffff;
}

.switch-node {
  background: linear-gradient(135deg, #ff6b6b 0%, #b10c0c 100%);
}

.switch-node .node-icon {
  background: #f13756;
  color: #ffffff;
}

.webhook-node {
  border-left: 4px solid #16a34a;
}

.webhook-node .node-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.email-node {
  border: 4px solid #cd3e3e;
  width: 110px;
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
  position: relative;
  transition: all 0.3s ease;
}

.email-node:hover {
  border-color: #ef4444;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.15);
}

.email-node .node-icon {
  position: relative;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #ffffff;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.email-node .icon-bg {
  position: relative;
  z-index: 1;
}

.email-node .action-badge {
  position: absolute;
  top: -8px;
  left: 8px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.3);
  z-index: 10;
}

.email-node .status-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
}

.email-node .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
  transition: all 0.3s ease;
}

.email-node .status-indicator.configured .status-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  animation: pulse-green 2s infinite;
}

.email-node .node-body {
  padding-top: 8px;
}

.email-node .node-details {
  position: absolute;
  bottom: 0px;
  right: 10px;
}

.email-node .provider-tag {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 4px;
  text-transform: uppercase;
}

.email-node .provider-tag.smtp {
  background: #dbeafe;
  color: #1d4ed8;
}

.email-node .provider-tag.mailgun {
  background: #fef3c7;
  color: #d97706;
}

.email-node .cc-indicator {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
}

@keyframes pulse-green {

  0%,
  100% {
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  }

  50% {
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
  }
}

.api-trigger-node {
  border-left: 6px solid #0ea5e9;
  border-color: #0ea5e9;
}

.api-trigger-node:hover {
  border-color: #75d3ff;
}

.api-trigger-node .node-icon {
  background: #f0f9ff;
  color: #0ea5e9;
}

.contact-agent-node {
  border-left: 6px solid #dc2626;
  border-color: #dc2626;
}

.contact-agent-node:hover {
  border-color: #ff8282;
}

.contact-agent-node .node-icon {
  background: #fef2f2;
  color: #dc2626;
}

.documents-node {
  background-color: #dbeafe;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.documents-node:hover {
  border: 4px solid #76b2ff !important;
  transition: all 0.2s ease;
}

.documents-node .node-icon {
  background: #f0f9ff;
  color: #0369a1;
}

.database-integration-node {
  background-color: #dcfce7;
  border: 4px solid #ffffff !important;
  transition: all 0.2s ease;
}

.database-integration-node:hover {
  border: 4px solid #67d89a !important;
  transition: all 0.2s ease;
}

.database-integration-node .node-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.knowledge-api-node {
  background-color: #ffecdb;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.knowledge-api-node:hover {
  border: 4px solid #ffca98 !important;
  transition: all 0.2s ease;
}

.knowledge-api-node .node-icon {
  color: #ff6c37;
}

.products-node {
  background-color: #fef3c7;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.products-node:hover {
  border: 4px solid #ffd68b !important;
  transition: all 0.2s ease;
}

.products-node .node-icon {
  background: #fef3c7;
  color: #d97706;
}

/* SharePoint Node Styling */
.sharepoint-node {
  background-color: #e6f7f8;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.sharepoint-node:hover {
  border: 4px solid #1a9ba1 !important;
  transition: all 0.2s ease;
}

.sharepoint-node .node-icon {
  background: #e6f7f8;
  color: #02787c;
}

/* Default Case Output Styling */
.switch-default-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
  border-radius: 0 0 18px 18px;
}

.default-output-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.default-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.default-output-number {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 6px rgba(234, 88, 12, 0.3);
}

.default-output-details {
  flex: 1;
  min-width: 0;
}

.default-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #9a3412;
  line-height: 1.3;
  margin-bottom: 2px;
}

.default-output-description {
  font-size: 0.75rem;
  color: #c2410c;
  line-height: 1.2;
  font-style: italic;
}

.default-handle-cell {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.default-output-handle {
  position: relative !important;
  right: -16px;
  margin-top: 14px;
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  border: 2px solid white !important;
  width: 16px !important;
  height: 16px !important;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.3);
}

/* Handle Styles - Modern Color Design */
.input-handle {
  height: 25px;
  width: 15px;
  border-radius: 2px;
  background: #f5f8ff;
  border: 3px solid #3173ff !important;
  border: none;
  left: -2px;
  /* box-shadow: 0 3px 12px rgba(79, 70, 229, 0.3); */
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  transform-origin: center;
}

.input-handle.connected {
  background: #3173ff !important;
}

.input-response-handle {
  height: 18px;
  width: 18px;
  background: #f5f8ff;
  border: 3px solid #f87171 !important;
  border: none;
  left: -2px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  transform-origin: center;
}

.input-response-handle:hover {
  background: #f87171;
}

.input-response-handle.connected {
  background: #f87171 !important;
}

.output-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #f59e0b;
  margin-right: -3px;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
}

.input-handle:hover {
  background: #3173ff;
  opacity: 1;
}

.output-handle:hover {
  background: #f59e0b;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Connected source handle styles - apply hover color when connected */
.output-handle.connected {
  background: #f59e0b !important;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.condition-true-output-handle {
  background: #006eff;
}

.condition-false-output-handle {
  background: #dc2626;
}

/* Special handle styles for start/end nodes */
.message-node .output-handle {
  background: #ffffff;
  border: 3px solid #21c90b;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.6);
}

.message-node .output-handle:hover {
  background: #21c90b;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.response-node .input-handle {
  background: #ffffff;
  border: 3px solid #f87171;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 12px rgba(248, 113, 113, 0.3);
}

.response-node .input-handle:hover {
  background: #f87171;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.api-node .input-handle,
.api-node .output-handle {
  background: #ffffff;
  border: 3px solid #f59e0b;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.6);
}

.api-node .input-handle:hover,
.api-node .output-handle:hover {
  background: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.multi-edge-handle {
  position: absolute;
  bottom: -14px;
  width: 17px;
  height: 17px;
  background: #ffffff;
  border: 3px solid #2d70ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.multi-edge-handle:hover {
  background: #2d70ff;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.intent-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #ab44ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.intent-model-handle:hover {
  background: #ab44ff;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

/* Additional connected handle styles for specific node types */
.message-node .output-handle.connected {
  background: #21c90b !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.response-node .input-handle.connected {
  background: #f87171 !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.api-node .input-handle.connected,
.api-node .output-handle.connected {
  background: #f59e0b !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.multi-edge-handle.connected {
  background: #2d70ff !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.intent-model-handle.connected {
  background: #ab44ff !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.condition-true-output-handle.connected {
  background: #006eff !important;
}

.condition-false-output-handle.connected {
  background: #dc2626 !important;
}

.multi-edge-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #3575ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.multi-edge-target-handle:hover {
  background: #3575ff;
}

.multi-edge-target-handle.connected {
  background: #3575ff !important;
}

.intentai-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #ab44ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.intentai-target-handle:hover {
  background: #ab44ff;
}

.intentai-target-handle.connected {
  background: #ab44ff !important;
}

/* Output Labels - Base Styles */
.output-labels {
  position: absolute;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  z-index: 50;
}

/* Intent Labels - Below the node, no background */
.intent-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Model Labels - Below the node, with background */
.model-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Classifier Labels - Positioned for diamond shape */
.classifier-labels {
  top: 10px;
  right: -60px;
  height: 100%;
  justify-content: space-around;
}

/* Loop Labels - Below the node, no background */
.loop-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Switch Labels - Below the node, no background */
.switch-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Condition Labels - Mixed positioning (right and bottom), no background */
.condition-labels {
  height: 100%;
  right: -15px;
}

.output-label {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  border: none;
  white-space: nowrap;
  text-align: center;
}

/* Intent Labels - No background */
.match-label {
  color: #10b981;
  background: transparent;
}

.nomatch-label {
  color: #ef4444;
  background: transparent;
}

.intent-list-item {
  position: relative !important;
  /* เปิดให้ children absolute ทำงาน */
  overflow: visible !important;
  /* ให้ลอยออกมาเห็นได้ */
  padding-right: 60px !important;
}

/* stack your red items vertically on the right */
.intent-action-list {
  position: absolute;
  right: 0;
  /* ชิดขอบขวาของ parent */
  top: 50%;
  /* จัดกึ่งกลางแนวตั้ง */
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
  pointer-events: none;
}

/* optional: tighten up the red dots */
.intent-action-list .v-list-item {
  width: 10px;
  height: 10px;
  margin-bottom: 0px;
  border-radius: 50%;
  background-color: red;
  padding: 0;
}

/* Dynamic Intent Output Labels */
.intent-labels .output-label {
  position: absolute;
  right: -50px;
  transform: translateY(-50%);
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  font-weight: 600;
  font-size: 10px;
  white-space: nowrap;
}

/* Different colors for multiple outputs */
.intent-labels .output-label-0 {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.2);
}

.intent-labels .output-label-1 {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
}

.intent-labels .output-label-2 {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.2);
}

.intent-labels .output-label-3 {
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
  border-color: rgba(139, 92, 246, 0.2);
}

/* Additional colors for more outputs */
.intent-labels .output-label:nth-child(n+5) {
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  border-color: rgba(99, 102, 241, 0.2);
}

/* Modern Minimal Model Labels */
.model-output-label {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  font-weight: 600;
  font-size: 11px;
}

.model-multi-label {
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  font-weight: 600;
  font-size: 11px;
}

/* Classifier Labels - With background */
.classifier-right-label {
  color: #ffffff;
  background: #10b981;
}

.classifier-bottom-label {
  color: #ffffff;
  background: #6b7280;
}

/* Loop Labels - No background */
.loop-continue-label {
  color: #eab308;
  background: transparent;
}

.loop-exit-label {
  color: #eab308;
  background: transparent;
}

/* Switch Labels - No background */
.switch-label-1 {
  color: #f43f5e;
  background: transparent;
}

.switch-label-2 {
  color: #f43f5e;
  background: transparent;
}

.switch-label-3 {
  color: #f43f5e;
  background: transparent;
}

/* Condition Labels - No background */
.condition-true-label {
  position: absolute;
  top: 0;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  font-weight: 600;
  font-size: 11px;
  top: 0;
}

.condition-false-label {
  position: absolute;
  bottom: 0;
  color: #ef4444;
  background: rgba(255, 98, 98, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.2);
  font-weight: 600;
  font-size: 11px;
}

/* Selection States */
.minimal-node.selected {
  /* border-color: #3b82f6; */
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Delete Button Styles - Enhanced */
.delete-button {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  z-index: 900;
  /* Higher z-index than handles to ensure it's always on top */
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.delete-button:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.delete-button .v-icon {
  color: white;
  font-size: 16px;
  font-weight: bold;
}

.n8n-node:hover .delete-button,
.minimal-node:hover .delete-button,
.decision-container:hover .delete-button,
.ai-agent-node:hover .delete-button,
.intent-modern-node:hover .delete-button,
.smart-intent-modern-node:hover .delete-button,
.guardrail-ai-node:hover .delete-button,
.switch-modern-node:hover .delete-button,
.intent-ai-node:hover .delete-button {
  opacity: 1;
}

/* Special positioning for diamond node */
.diamond-delete {
  top: -12px;
  right: -12px;
}

/* Warning Badge Styles */
.warning-badge {
  position: absolute;
  bottom: -15px;
  right: -8px;
  width: 35px;
  height: 35px;
  background: #f59e0b;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 950;
  /* Higher than handles but lower than delete button */
  box-shadow: 0 0px 20px rgba(245, 158, 11, 1);
  /* animation: warningPulse 2s infinite; */
}


.warning-badge .v-icon {
  color: white;
  font-size: 16px;
  font-weight: bold;
  animation: warningPulse 2s infinite;
}
/* Warning badge animation */
@keyframes warningPulse {

  0%,
  100% {
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
    transform: scale(1);
  }

  50% {
    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.6);
    transform: scale(1.3);
  }
}

/* Focus states for accessibility */
.n8n-node:focus-within {
  /* outline: 2px solid #3b82f6; */
  outline-offset: 2px;
}

.decision-container:focus-within {
  /* outline: 2px solid #3b82f6; */
  outline-offset: 4px;
}

/* Dragging State Styles - Card-like lifting and rotating effect for individual nodes */
/* Use Vue Flow's built-in dragging classes for specific node dragging */
.vue-flow__node.dragging .n8n-node,
.vue-flow__node[data-dragging="true"] .n8n-node,
.n8n-node.dragging {
  transform: translateY(-8px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18) !important;
  z-index: 1000;
  /* border-color: #3b82f6; */
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .decision-container .decision-node,
.vue-flow__node[data-dragging="true"] .decision-container .decision-node,
.decision-container.dragging .decision-node {
  transform: translateY(-12px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  /* border-color: #3b82f6; */
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .minimal-node,
.vue-flow__node[data-dragging="true"] .minimal-node,
.minimal-node.dragging {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  z-index: 1000;
  /* border-color: #3b82f6; */
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .decision-container::before,
.vue-flow__node[data-dragging="true"] .decision-container::before,
.decision-container.dragging::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  z-index: -1;
  opacity: 0.5;
}

/* Gentle pulse animation for handles during drag */
@keyframes gentlePulse {

  0%,
  100% {
    opacity: 0.9;
  }

  50% {
    opacity: 1;
  }
}

/* Delete button gentle animation during drag for individual nodes */
.vue-flow__node.dragging .delete-button,
.vue-flow__node[data-dragging="true"] .delete-button,
.delete-button.dragging {
  transform: scale(1.1);
  opacity: 1;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
  animation: gentleFloat 3s ease-in-out infinite;
}

/* Gentle floating animation for delete button */
@keyframes gentleFloat {

  0%,
  100% {
    transform: scale(1.1) translateY(0px);
  }

  50% {
    transform: scale(1.1) translateY(-2px);
  }
}

/* Node text gentle enhancement during drag for individual nodes */
.vue-flow__node.dragging .node-title,
.vue-flow__node[data-dragging="true"] .node-title,
.node-title.dragging {
  /* color: #1e40af; */
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.vue-flow__node.dragging .ai-agent-title,
.vue-flow__node[data-dragging="true"] .ai-agent-title,
.ai-agent-title.dragging {
  /* color: #1e40af; */
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.vue-flow__node.dragging .decision-label,
.vue-flow__node[data-dragging="true"] .decision-label,
.decision-label.dragging {
  /* color: #1e40af; */
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Minimal dragging effects for start/end nodes */
.vue-flow__node.dragging .message-node,
.vue-flow__node[data-dragging="true"] .message-node,
.message-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.16) !important;
}

.vue-flow__node.dragging .api-node,
.vue-flow__node[data-dragging="true"] .api-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(245, 158, 11, 0.18) !important;
}

.vue-flow__node.dragging .response-node,
.vue-flow__node[data-dragging="true"] .response-node,
.response-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.18) !important;
}

.vue-flow__node.dragging .intent-modern-node,
.vue-flow__node[data-dragging="true"] .intent-modern-node,
.vue-flow__node.dragging .smart-intent-modern-node,
.vue-flow__node[data-dragging="true"] .smart-intent-modern-node,
.response-node.dragging {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 8px 8px 15px rgba(98, 0, 255, 0.2) !important;
}

.vue-flow__node.dragging .ai-agent-node,
.vue-flow__node[data-dragging="true"] .ai-agent-node,
.response-node.dragging {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 6px 9px 15px rgba(11, 68, 255, 0.493) !important;
}

/* Prefer reduced motion for users that request it */
@media (prefers-reduced-motion: reduce) {

  .n8n-node,
  .minimal-node,
  .ai-agent-node,
  .intent-modern-node,
  .message-node,
  .api-node,
  .response-node {
    transition: none !important;
    animation: none !important;
  }
}

/* Modern Switch Node */
.switch-modern-node {
  background: linear-gradient(135deg, #fcf8f8 0%, #e2e8f0 100%);
  border: 2px solid #f1636379;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-width: 200px;
  cursor: pointer;
  margin: 15px;
  overflow: visible;
}

.switch-modern-node:hover {
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  border-color: #f34646;
}

.selected .switch-modern-node {
  border-color: #f34646;
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Switch Header Section */
.switch-header-section {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(135deg, #ff6464 0%, #e90000 100%);
  border-radius: 18px 18px 0 0;
}

.switch-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: transparent;
  color: white;
  box-shadow: 0 4px 12px rgba(241, 99, 99, 0.3);
}

.switch-info {
  flex: 1;
  min-width: 0;
}

.switch-title {
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.2;
  margin-bottom: 2px;
}

.switch-subtitle {
  font-size: 0.8rem;
  color: #ffffff;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Switch Outputs Container */
.switch-outputs-container {
  padding: 0;
}

.switch-table {
  width: 100%;
  border-collapse: collapse;
}

.switch-table .handle-row {
  border: none;
}

.switch-table .handle-row:hover {
  background: rgba(99, 102, 241, 0.04);
}

.switch-output-cell {
  padding: 8px 16px;
  vertical-align: middle;
  border: none;
}

.switch-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.output-number {
  flex-shrink: 0;
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
}

.switch-output-details {
  flex: 1;
  min-width: 0;
}

.switch-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-output-condition {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
}

.handle-cell {
  padding: 8px 16px 8px 0;
  vertical-align: middle;
  width: 80px;
  border: none;
}

.handle-content.switch-handle {
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
}

.switch-output-handle {
  right: -16px;
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  border: 2px solid white !important;
  width: 16px !important;
  height: 16px !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.switch-output-handle .handle-label {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(99, 102, 241, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.switch-output-handle:hover .handle-label {
  opacity: 1;
}


/* Intent Fallback Output Styling */
.intent-fallback-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #f9edff;
  border-radius: 0 0 12px 12px;
}

.fallback-output-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.fallback-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.fallback-output-number {
  background: #b563f8 !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 6px rgba(182, 12, 234, 0.3);
}

.fallback-output-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  width: 100%;
}

.fallback-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #42129a;
  line-height: 1.3;
  margin-bottom: 2px;
}

.fallback-output-description {
  margin-top: -6px;
  margin-right: -18px;
  font-size: 0.75rem;
  color: #7f0cc2;
  line-height: 1.2;
  font-style: italic;
}

.fallback-handle-cell {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.fallback-output-handle {
  position: relative !important;
  right: -19px;
  margin-top: 14px;
  background: #ffffff !important;
  border: 3px solid #f59e0b !important;
  width: 18px !important;
  height: 18px !important;
  cursor: pointer;
}

.fallback-output-handle:hover {
  background: #f59e0b !important;
}

/* Connected styles for switch and fallback handles */
.switch-output-handle.connected {
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.fallback-output-handle.connected {
  background: #f59e0b !important;
}

/* Smart Intent Node Styles - Based on Intent Node */
.smart-intent-modern-node {
  background: white;
  border: 5px solid #4f46e5;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(79, 70, 229, 0.4);
  transition: all 0.2s ease;
  position: relative;
  min-width: 300px;
  width: 300px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
  overflow: visible;
}

.smart-intent-modern-node:hover {
  border-color: #3730a3;
  box-shadow: 2px 6px 12px rgba(79, 70, 229, 0.6);
}

.selected .smart-intent-modern-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Smart Intent Header Section */
.smart-intent-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.smart-intent-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.smart-intent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(79, 70, 229, 0.25);
}

.smart-intent-info {
  flex: 1;
  min-width: 0;
}

.smart-intent-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.smart-intent-subtitle {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Knowledge Sources Indicators */
.knowledge-sources-indicators {
  margin-top: 4px;
}

.knowledge-icons-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.knowledge-icon {
  transition: all 0.2s ease;
  cursor: help;
}

.knowledge-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Smart Intents Container */
.smart-intents-container {
  padding: 8px 0px 8px 16px;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.smart-intent-table {
  width: 100%;
  border-collapse: collapse;
}

.smart-intent-cell {
  padding: 8px 0;
  vertical-align: middle;
  border: none;
}

.smart-intent-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-right: 10px;
}

.smart-intent-number {
  background: #4f46e5 !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.smart-intent-details {
  flex: 1;
  min-width: 0;
}

.smart-intent-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.ai-config-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Smart Intent Action Handles */
.smart-intent-action-handle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.smart-intent-action-handle.main-action-handle {
  background: #ffffff;
  border: 3px solid #4f46e5;
}

.smart-intent-action-handle.main-action-handle:hover {
  background: #4f46e5;
}

.smart-intent-action-handle.main-action-handle.connected {
  background: #4f46e5 !important;
}

/* Smart Intent Fallback Output Styling */
.smart-intent-fallback-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #eef2ff;
  border-radius: 0 0 12px 12px;
}

.smart-intent-fallback-output .fallback-output-number {
  background: #4f46e5 !important;
  color: white !important;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.smart-intent-fallback-output .fallback-output-name {
  color: #312e81;
}

.smart-intent-fallback-output .fallback-output-description {
  color: #4338ca;
}

/* Smart Intent Model Handle */
.smart-intent-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #4f46e5 !important;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.smart-intent-model-handle:hover {
  background: #4f46e5;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.smart-intent-model-handle.connected {
  background: #4f46e5 !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.default-output-handle.connected {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.3);
}

/* AI Agent Node Styles */
.ai-agent-node {
  min-width: 200px;
  max-width: 280px;
}

.ai-agent-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
}

.ai-agent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #5e9cff 0%, #1857df 100%);
  color: white;
  flex-shrink: 0;
}

.ai-agent-text {
  flex: 1;
  min-width: 0;
}

.ai-agent-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.ai-agent-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
  line-height: 1.3;
}

.base-prompt-indicator {
  position: absolute;
  bottom: 3px;
  right: 3px;
  display: flex;
  align-items: center;
  color: #00ff2a;
  font-weight: 600;
  background: rgba(227, 255, 227, 0.95);
  padding: 1px 2px;
  border-radius: 5px;
  border: 1px solid rgb(76, 184, 93);
  box-shadow: 2px 2px 4px rgb(33, 116, 34);
  width: fit-content;
  z-index: 10;
}

.base-prompt-indicator span {
  line-height: 1;
}

.ai-agent-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.provider-info,
.model-info {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #475569;
  font-weight: 500;
}

.provider-info {
  color: #3b82f6;
}

.model-info {
  color: #8b5cf6;
}

.provider-info .v-icon,
.model-info .v-icon {
  opacity: 0.7;
}

/* Responsive styles for Smart Intent */
@media (max-width: 768px) {
  .smart-intent-modern-node {
    min-width: 280px;
    width: 280px;
  }

  .smart-intent-content {
    gap: 8px;
  }

  .smart-intent-name {
    font-size: 0.8rem;
  }

  .ai-config-info {
    font-size: 10px;
  }
}
</style>